import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'
import AdminDashboard from './AdminDashboard'
import TeacherCodeForm from './TeacherCodeForm'
import TrialSettingsRow from './TrialSettingsRow'
import { t, tEn } from '../i18n/translations'

const APP_LANGUAGES = [
  { key: 'tigrinya', label: 'ትግርኛ' },
  { key: 'amharic', label: 'አማርኛ' },
  { key: 'latin', label: 'English' },
]

const DEFAULT_FONT_SIZE = 1.15
const MIN_FONT_SIZE = 0.8
const MAX_FONT_SIZE = 2.0
const STEP = 0.05

export default function Settings({ onClose, previewText }) {
  const { isAdmin, hasLifetimeTeacherAccess } = useAuth()
  const { appLanguage, changeAppLanguage } = useLanguage()
  const [showAdmin, setShowAdmin] = useState(false)
  const [showTeacherCode, setShowTeacherCode] = useState(false)
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('sk_font_size')
    return saved ? parseFloat(saved) : DEFAULT_FONT_SIZE
  })

  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('sk_dark_mode') === 'true'
  })

  const [showCredits, setShowCredits] = useState(false)

  // Apply font size
  useEffect(() => {
    localStorage.setItem('sk_font_size', fontSize.toString())
    document.documentElement.style.setProperty('--geez-font-size', `${fontSize}rem`)
  }, [fontSize])

  // Apply dark mode
  useEffect(() => {
    localStorage.setItem('sk_dark_mode', darkMode.toString())
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light')
  }, [darkMode])

  const displaySize = (fontSize * 16).toFixed(1)

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={e => e.stopPropagation()}>
        <div className="settings-header">
          <button className="settings-back" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h2 className="settings-title">Settings</h2>
          <button className="settings-close" onClick={onClose} title="Close" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Font Size */}
        <div className="settings-section">
          <div className="settings-label-row">
            <span className="settings-label">Font Size</span>
            <span className="settings-value">{displaySize}</span>
          </div>
          <input
            type="range"
            className="settings-slider"
            min={MIN_FONT_SIZE}
            max={MAX_FONT_SIZE}
            step={STEP}
            value={fontSize}
            onChange={e => setFontSize(parseFloat(e.target.value))}
          />
          <div className="settings-preview">
            <p className="settings-preview-text" style={{ fontSize: `${fontSize}rem` }}>
              {previewText || 'አምላክና ብሩክ ንጉስ ኤየ'}
            </p>
          </div>
        </div>

        <div className="settings-divider" />

        {/* Dark Mode */}
        <div className="settings-section settings-row">
          <span className="settings-label">Dark Mode</span>
          <button
            className={`settings-toggle ${darkMode ? 'active' : ''}`}
            onClick={() => setDarkMode(!darkMode)}
            role="switch"
            aria-checked={darkMode}
          >
            <span className="settings-toggle-knob" />
          </button>
        </div>

        <div className="settings-divider" />

        {/* Language */}
        <div className="settings-section">
          <span className="settings-label" title={tEn('langPickerTitle')}>{t('langPickerTitle')}</span>
          <div className="settings-lang-options">
            {APP_LANGUAGES.map(lang => (
              <button
                key={lang.key}
                className={`settings-lang-btn ${appLanguage === lang.key ? 'active' : ''}`}
                onClick={() => changeAppLanguage(lang.key)}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>

        <div className="settings-divider" />

        {/* Premium: the trial offer, the days remaining, or why neither is
            shown. Rendered above teacher access because it is what most
            visitors are deciding about. */}
        <div className="settings-section">
          <TrialSettingsRow />
        </div>

        <div className="settings-divider" />

        {/* Teacher access: the standing status if they have it, otherwise a way
            to redeem a code. Labels here stay English, like the sections above. */}
        <div className="settings-section">
          {hasLifetimeTeacherAccess ? (
            <div className="settings-row">
              <span className="settings-label">Teacher Access</span>
              <span className="settings-value settings-lifetime" title={tEn('teacherLifetimeTitle')}>
                {t('teacherLifetimeTitle')}
              </span>
            </div>
          ) : (
            <>
              {/* The row stays put and toggles, like Credits below. Replacing it
                  with the form left nothing on screen that said how to close
                  it: the only way back was a button reading "Continue with
                  free lessons", which looks like an app action rather than a
                  way to collapse the section. */}
              <button
                className="settings-row settings-credits-btn"
                onClick={() => setShowTeacherCode(!showTeacherCode)}
                aria-expanded={showTeacherCode}
              >
                <span className="settings-label" title={tEn('teacherCodeLink')}>{t('teacherCodeLink')}</span>
                <svg
                  className={`settings-chevron ${showTeacherCode ? 'open' : ''}`}
                  width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
              {showTeacherCode && (
                <TeacherCodeForm showDismiss={false} onDone={() => setShowTeacherCode(false)} />
              )}
            </>
          )}
        </div>

        <div className="settings-divider" />

        {/* Credits */}
        <div className="settings-section">
          <button className="settings-row settings-credits-btn" onClick={() => setShowCredits(!showCredits)}>
            <span className="settings-label">Credits</span>
            <svg
              className={`settings-chevron ${showCredits ? 'open' : ''}`}
              width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
          {showCredits && (
            <div className="settings-credits">
              <p><strong>Developer:</strong> Yemane Russom</p>
              <p><strong>Kidase Audio:</strong> Kesis Yohannes Negassi</p>
            </div>
          )}
        </div>

        {isAdmin && (
          <>
            <div className="settings-divider" />
            <div className="settings-section">
              <button className="settings-row settings-admin-btn" onClick={() => setShowAdmin(true)}>
                <span className="settings-label">Admin Dashboard</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

      {showAdmin && <AdminDashboard onClose={() => setShowAdmin(false)} />}
    </div>
  )
}

// Initialize settings on app load (call once from App.jsx)
export function initSettings() {
  const fontSize = localStorage.getItem('sk_font_size')
  if (fontSize) {
    document.documentElement.style.setProperty('--geez-font-size', `${fontSize}rem`)
  }
  const darkMode = localStorage.getItem('sk_dark_mode') === 'true'
  document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light')
}
